import React, { ReactNode } from 'react';
import {
  AccountBookOutlined,
  BankOutlined,
  CarOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  HomeOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { BreadcrumbItem } from '../../../contexts/LayoutContext';
import CompanySelector from '../CompanySelector/CompanySelector';

interface SectionMeta {
  name: string;
  icon: ReactNode;
}

// Top-level sections — keyed on the first URL segment, mirroring the icons
// used in the sidebar nav so the breadcrumb's parent crumb is visually
// identical to the menu item that owns this page.
const SECTION_META: Record<string, SectionMeta> = {
  home: { name: 'Home', icon: <HomeOutlined /> },
  dealers: { name: 'Dealers', icon: <ShopOutlined /> },
  loans: { name: 'Loans', icon: <BankOutlined /> },
  floorplans: { name: 'Floorplans', icon: <CarOutlined /> },
  'borrowing-base': { name: 'Borrowing Base', icon: <AccountBookOutlined /> },
  imports: { name: 'Imports', icon: <CloudUploadOutlined /> },
  reports: { name: 'Reports', icon: <FileTextOutlined /> },
  admin: { name: 'Admin', icon: <SettingOutlined /> },
  settings: { name: 'Settings', icon: <SettingOutlined /> },
  support: { name: 'Support', icon: <FileTextOutlined /> },
  'video-training': { name: 'Video Training', icon: <PlayCircleOutlined /> },
  user: { name: 'User', icon: <UserOutlined /> },
};

// Sections whose URLs embed a company id as the second segment
// (e.g. /loans/14/active-loans). These get a CompanySelector dropdown crumb
// inserted after the section root.
const COMPANY_SCOPED_SECTIONS = new Set([
  'borrowing-base',
  'dealers',
  'floorplans',
  'imports',
  'loans',
]);

// Friendly labels for slugs that don't humanize cleanly (acronyms,
// brand names, ampersands). Anything not in this map falls through to the
// default kebab-case → Title Case converter.
const SLUG_LABEL_OVERRIDES: Record<string, string> = {
  'dms-gps-info': 'DMS & GPS Info',
  'vin-search': 'VIN Search',
  'vin-to-stock-xref': 'VIN to Stock# Xref',
  'ar-aged': 'AR Aged',
  'ar-balance': 'AR Balance',
  'ar-aging': 'AR Aging',
  'current-dealer-ar': 'Current Dealer AR',
  'bhph-loan-requests': 'BHPH Loan Requests',
  'acc-depreciation': 'Acc Depreciation',
  'dcs-api': 'DCS API',
  'ai-assistants': 'AI Assistants',
  'lp-est-billing-detail': 'LP Est Billing Detail',
  'lp-daily-log': 'LP Daily Log',
  'docusign-status': 'DocuSign Status',
  'docusign-docs': 'DocuSign Docs',
  'loan-to-lease-matchup': 'Loan to Lease Matchup',
  'matchup-exceptions': 'Matchup Exceptions',
  'loan-match-status': 'Loan Match Status',
  'pending-funding': 'Pending Funding',
  'pending-queue': 'Pending Queue',
  'pending-repo-swaps': 'Pending Repo Swaps',
  'active-loans': 'Active Loans',
  'active-vehicles': 'Active Vehicles',
  'paid-off-loans': 'Paid Off Loans',
  'paid-off-vehicles': 'Paid Off Vehicles',
  'payoff-requests': 'Payoff Requests',
  'payoff-requests-list': 'Payoff Requests List',
  'payoff-calculator': 'Payoff Calculator',
  'loan-calculator': 'Loan Calculator',
  'loan-plan-payoff-requests': 'Loan Plan Payoff Requests',
  'close-full-term-loans': 'Close Full Term Loans',
  'funding-log': 'Funding Log',
  'billing-payment-log': 'Billing/Payment Log',
  'balance-history': 'Balance History',
  'assign-lender': 'Assign Lender',
  'move-loans-to-floorplan': 'Move Loans To Floorplan',
  'bulk-payoff-requests': 'Bulk Payoff Requests',
  'exceptions-maintenance': 'Exceptions Maintenance',
  'corporate-docs': 'Corporate Docs',
  'portal-users': 'Portal Users',
  'unit-projections': 'Unit Projections',
  'dealer-borrowing-base': 'Dealer Borrowing Base',
  'billing-options': 'Billing Options',
  'billing-logs': 'Billing Logs',
  'borrowing-base-reports': 'Borrowing Base Reports',
  'bulk-loanplan-billing': 'Bulk Loanplan Billing',
  'prime-rate-history': 'Prime Rate History',
  'news-info': 'News & Info',
  'lease-quote': 'Lease Quote',
  'phone-directory': 'Phone Directory',
  'release-notes': 'Release Notes',
  'letter-templates': 'Letter Templates',
  'email-templates': 'Email Templates',
  'text-templates': 'Text Templates',
  'robo-templates': 'Robo Templates',
  'staff-users': 'Staff Users',
  'doc-library': 'Document Library',
  'buyer-names': 'Buyer Names',
  'video-management': 'Video Management',
  'bug-reports': 'Bug Reports',
  'login-log': 'Login Log',
  'imports-monitor': 'Imports Monitor',
  'ftp-status': 'FTP Status',
  'database-info': 'Database Info',
  'black-book': 'Black Book',
  'all-reports': 'All Reports',
  'email-reports': 'Email Reports',
  'credit-lines': 'Credit Lines',
  'floor-plans': 'Floor Plans',
  'floorplan-request': 'Floorplan Request',
  'loan-requests': 'Loan Requests',
  'premier-loan-requests': 'Premier Loan Requests',
  'indirect-loan-requests': 'Indirect Loan Requests',
  'refi-swap-premier-conversion': 'Refi/Swap/Premier Conversion',
  'check-for-duplicates': 'Check for Duplicates',
  'unsold-inventory': 'Unsold Inventory',
  'wholesale-inventory': 'Wholesale Inventory',
  'interest-balance': 'Interest Balance',
  'add-vehicle': 'Add Vehicle',
  'add-vendor': 'Add Vendor',
  'add-loan': 'Add Loan',
  'repo-swap': 'Repo Swap',
  'cancel-funding': 'Cancel Funding',
  'cancel-loan-requests': 'Cancel Loan Requests',
  'cancel-swap-request': 'Cancel Swap Request',
  'tranche-exceptions': 'Tranche Exceptions',
  'fund-loans': 'Fund Loans',
  'pending-funding-detail': 'Pending Funding Detail',
};

const isNumericId = (segment: string): boolean => /^\d+$/.test(segment);

const humanize = (slug: string): string => {
  if (SLUG_LABEL_OVERRIDES[slug]) return SLUG_LABEL_OVERRIDES[slug];
  return slug
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/**
 * Build a default breadcrumb trail from the current pathname. Used by
 * MainLayout when a page hasn't called setBreadcrumbs() — keeps every
 * authenticated page aligned with the same icon-and-dropdown pattern.
 *
 * Examples:
 *   /admin/users                 → Admin > Users
 *   /home/news-info              → Home > News & Info
 *   /loans/14/active-loans       → Loans > [Co Switcher] > Active Loans
 *   /loans/14/active-loans/detail/123
 *                                → Loans > [Co Switcher] > Active Loans > Detail
 *   /dealers/14/contacts/5/edit  → Dealers > [Co Switcher] > Contacts > Edit
 */
export const deriveBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  const rawSegments = pathname.split('/').filter(Boolean);
  if (rawSegments.length === 0) return [];

  const sectionKey = rawSegments[0];
  const sectionMeta = SECTION_META[sectionKey];
  if (!sectionMeta) return [];

  const sectionPath = `/${sectionKey}`;
  const items: BreadcrumbItem[] = [
    { title: sectionMeta.name, path: sectionPath, icon: sectionMeta.icon },
  ];

  let cursor = 1;

  // Company-scoped routes embed the id as the second segment.
  if (
    COMPANY_SCOPED_SECTIONS.has(sectionKey) &&
    rawSegments[cursor] &&
    isNumericId(rawSegments[cursor])
  ) {
    items.push({ title: <CompanySelector variant="breadcrumb" /> });
    cursor += 1;
  }

  // Remaining segments become the page tail. Each non-id, non-passthrough
  // segment becomes its own crumb, with intermediate crumbs linkable so
  // users can step back up the tree.
  let runningPath = items[items.length - 1]?.path ?? sectionPath;
  if (COMPANY_SCOPED_SECTIONS.has(sectionKey) && cursor === 2) {
    runningPath = `${sectionPath}/${rawSegments[1]}`;
  }

  const tailSegments = rawSegments.slice(cursor);
  const meaningful = tailSegments.filter(seg => !isNumericId(seg));

  // Walk through the actual segments (incl. ids) so runningPath stays
  // accurate, but only emit crumbs for slug-style segments — numeric ids
  // are noise in a breadcrumb.
  let emitted = 0;
  for (let i = 0; i < tailSegments.length; i += 1) {
    const seg = tailSegments[i];
    runningPath = `${runningPath}/${seg}`;
    if (isNumericId(seg)) continue;

    const isLast = emitted === meaningful.length - 1;
    items.push({
      title: humanize(seg),
      path: isLast ? undefined : runningPath,
    });
    emitted += 1;
  }

  // If we never got past the section (e.g. /admin landing page), drop the
  // section's path so it renders as the current page rather than a link.
  if (items.length === 1) {
    items[0] = { ...items[0], path: undefined };
  }

  return items;
};
